export type ReorderableColumn = {
  id: string;
  isReorderable?: boolean;
};

export type ColumnDropPosition = 'before' | 'after' | 'on';

export function isReorderableColumn(column: ReorderableColumn) {
  return column.isReorderable !== false;
}

export function reorderableSlots<T extends ReorderableColumn>(columns: readonly T[]) {
  return columns.flatMap((column, index) => (isReorderableColumn(column) ? [{ column, index }] : []));
}

export function reorderableNeighborId<T extends ReorderableColumn>(
  columns: readonly T[],
  id: string,
  direction: 'previous' | 'next',
) {
  const slots = reorderableSlots(columns);
  const index = slots.findIndex((slot) => slot.column.id === id);
  if (index === -1) {
    return undefined;
  }
  const neighbor = direction === 'previous' ? slots[index - 1] : slots[index + 1];
  return neighbor?.column.id;
}

export function moveWithinReorderableSlots<T extends ReorderableColumn>(
  columns: readonly T[],
  id: string,
  targetId: string,
  position: 'before' | 'after',
) {
  const slots = reorderableSlots(columns);
  const ids = slots.map((slot) => slot.column.id);
  const fromIndex = ids.indexOf(id);
  const targetIndex = ids.indexOf(targetId);
  if (fromIndex === -1 || targetIndex === -1 || fromIndex === targetIndex) {
    return undefined;
  }

  ids.splice(fromIndex, 1);
  const targetAfterRemoval = ids.indexOf(targetId);
  const insertAt = position === 'before' ? targetAfterRemoval : targetAfterRemoval + 1;
  ids.splice(insertAt, 0, id);

  const next = columns.map((column) => column.id);
  slots.forEach((slot, index) => {
    next[slot.index] = ids[index];
  });

  const changed = next.some((nextId, index) => nextId !== columns[index].id);
  return changed ? next : undefined;
}

export function orderAfterVisualMove<T extends ReorderableColumn & PinnableColumn>(
  orderedColumns: readonly T[],
  visibleColumns: readonly T[],
  id: string,
  direction: 'left' | 'right',
) {
  const visual = layoutVisibleColumns([...visibleColumns]).columns;
  const index = visual.findIndex((column) => column.id === id);
  const neighbor = visual[direction === 'left' ? index - 1 : index + 1];
  if (!neighbor || !isReorderableColumn(visual[index]) || !isReorderableColumn(neighbor)) {
    return undefined;
  }

  const desired = visual.map((column) => column.id);
  const swapIndex = direction === 'left' ? index - 1 : index + 1;
  [desired[index], desired[swapIndex]] = [desired[swapIndex], desired[index]];

  const visibleIds = new Set(visibleColumns.map((column) => column.id));
  let desiredIndex = 0;
  const next = orderedColumns.map((column) => {
    if (!visibleIds.has(column.id)) {
      return column.id;
    }
    const nextId = desired[desiredIndex];
    desiredIndex += 1;
    return nextId;
  });
  const byId = new Map(orderedColumns.map((column) => [column.id, column]));
  const nextVisible = next.flatMap((nextId) => {
    const column = byId.get(nextId);
    return column && visibleIds.has(nextId) ? [column] : [];
  });
  const nextVisual = layoutVisibleColumns(nextVisible).columns.map((column) => column.id);
  const nextIndex = nextVisual.indexOf(id);
  const moved = direction === 'left' ? nextIndex === index - 1 : nextIndex === index + 1;
  return moved ? next : undefined;
}

export type ColumnDropSlot = {
  columnId: string;
  position: 'before' | 'after';
};

export function dropSlotsForColumn<T extends ReorderableColumn>(columns: readonly T[], movingId: string) {
  const ids = reorderableSlots(columns).map((slot) => slot.column.id);
  const fromIndex = ids.indexOf(movingId);
  if (fromIndex === -1 || ids.length < 2) {
    return [];
  }

  const remaining = ids.filter((id) => id !== movingId);
  const slots: ColumnDropSlot[] = [];
  for (let insertAt = 0; insertAt <= remaining.length; insertAt++) {
    if (insertAt === fromIndex) {
      continue;
    }
    if (insertAt === 0) {
      slots.push({ columnId: remaining[0], position: 'before' });
    } else {
      slots.push({ columnId: remaining[insertAt - 1], position: 'after' });
    }
  }
  return slots;
}

export function dropPositionFromPointer(
  clientX: number,
  left: number,
  width: number,
  direction: 'ltr' | 'rtl',
): Exclude<ColumnDropPosition, 'on'> {
  if (width > 0) {
    const ratio = (clientX - left) / width;
    const startRatio = direction === 'rtl' ? 1 - ratio : ratio;
    return startRatio < 0.5 ? 'before' : 'after';
  }

  const isBeforeMidpoint = clientX < left + width / 2;
  if (direction === 'rtl') {
    return isBeforeMidpoint ? 'after' : 'before';
  }
  return isBeforeMidpoint ? 'before' : 'after';
}

export type ColumnPin = 'start' | 'end';

export type PinnableColumn = {
  pin?: ColumnPin;
};

export type RowHeaderColumn = {
  isRowHeader?: boolean;
};

export function withRowHeaderFallback<T extends RowHeaderColumn>(columns: T[]): T[] {
  if (columns.length === 0 || columns.some((column) => column.isRowHeader)) {
    return columns;
  }

  return columns.map((column, index) => (index === 0 ? { ...column, isRowHeader: true } : column));
}

export function layoutVisibleColumns<T extends PinnableColumn>(columns: T[]) {
  const usesIdentityPin = columns.some((column) => column.pin === 'start' || column.pin === 'end');
  if (!usesIdentityPin) {
    return { columns, usesIdentityPin: false };
  }

  const start = columns.filter((column) => column.pin === 'start');
  const end = columns.filter((column) => column.pin === 'end');
  const middle = columns.filter((column) => column.pin !== 'start' && column.pin !== 'end');
  return { columns: [...start, ...middle, ...end], usesIdentityPin: true };
}

export function scrollOverflow(scrollLeft: number, scrollWidth: number, clientWidth: number) {
  return {
    start: scrollLeft > 1,
    end: scrollLeft < scrollWidth - clientWidth - 1,
  };
}

export type PromotionColumn = {
  id: string;
  minWidth: number;
};

export type ColumnPromotionInput = {
  containerWidth: number;
  nameColumnId: string;
  nameMinWidth: number;
  chromeWidth: number;
  reserved: PromotionColumn[];
  candidates: PromotionColumn[];
};

export type ColumnPromotionResult = {
  promotedIds: string[];
  foldedIds: string[];
  widths: Record<string, number>;
};

export function computeColumnPromotion({
  containerWidth,
  nameColumnId,
  nameMinWidth,
  chromeWidth,
  reserved,
  candidates,
}: ColumnPromotionInput): ColumnPromotionResult {
  let budget = containerWidth - nameMinWidth - reserved.reduce((sum, column) => sum + column.minWidth, 0) - chromeWidth;
  const promotedIds: string[] = [];
  const foldedIds: string[] = [];
  let folding = false;

  for (const candidate of candidates) {
    if (!folding && candidate.minWidth <= budget) {
      promotedIds.push(candidate.id);
      budget -= candidate.minWidth;
    } else {
      folding = true;
      foldedIds.push(candidate.id);
    }
  }

  let sumMins = nameMinWidth + reserved.reduce((sum, column) => sum + column.minWidth, 0);
  for (const id of promotedIds) {
    const candidate = candidates.find((column) => column.id === id);
    if (candidate) {
      sumMins += candidate.minWidth;
    }
  }

  const flexibleWidth = containerWidth - chromeWidth;
  const scale = sumMins > 0 ? Math.max(1, flexibleWidth / sumMins) : 1;
  const scaled = (minWidth: number) => Math.floor(minWidth * scale);
  const widths: Record<string, number> = {};
  let fixed = chromeWidth;

  for (const column of reserved) {
    const width = scaled(column.minWidth);
    widths[column.id] = width;
    fixed += width;
  }

  for (const id of promotedIds) {
    const candidate = candidates.find((column) => column.id === id);
    if (!candidate) {
      continue;
    }
    const width = scaled(candidate.minWidth);
    widths[id] = width;
    fixed += width;
  }

  widths[nameColumnId] = Math.max(Math.floor(containerWidth - fixed), scaled(nameMinWidth));

  return { promotedIds, foldedIds, widths };
}

export function widthsForDisplayedColumns(
  widths: Record<string, number>,
  displayedIds: readonly string[],
  absorbId: string | undefined,
) {
  const displayed = new Set(displayedIds);
  const next: Record<string, number> = {};
  if (!absorbId || !displayed.has(absorbId)) {
    for (const id of displayedIds) {
      if (widths[id] !== undefined) {
        next[id] = widths[id];
      }
    }
    return next;
  }

  let absorbed = widths[absorbId] ?? 0;
  for (const [id, width] of Object.entries(widths)) {
    if (id !== absorbId && !displayed.has(id)) {
      absorbed += width;
    }
  }

  for (const id of displayedIds) {
    if (id === absorbId) {
      next[id] = absorbed;
    } else if (widths[id] !== undefined) {
      next[id] = widths[id];
    }
  }

  return next;
}
